import { useCallback, useEffect, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import {
  createResignReason,
  deactivateResignReason,
  listResignReasons,
  updateResignReason,
} from "../services/onboarding/resignReasons";
import { ResignReason } from "../types/onboarding";

interface UseResignReasons {
  reasons: ResignReason[];
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  create: (code: string, displayLabel: string) => Promise<boolean>;
  updateLabel: (code: string, displayLabel: string) => Promise<boolean>;
  deactivate: (code: string) => Promise<void>;
}

const useResignReasons = (toastRef: React.RefObject<Toast>): UseResignReasons => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [reasons, setReasons] = useState<ResignReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await listResignReasons(false);
      setReasons(data?.reasons ?? []);
    } catch (e) {
      console.error("Failed to load resign reasons", e);
      setError(true);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load resign reasons.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toastRef]);

  useEffect(() => {
    reload();
  }, [reload]);

  // create returns a boolean and re-throws nothing; duplicate-code (409) is
  // surfaced as an error toast and the dialog keeps its own inline handling.
  const create = useCallback(
    async (code: string, displayLabel: string): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await createResignReason({ code, display_label: displayLabel });
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Reason added.",
        errorMessage: "Could not add the reason (the code may already exist).",
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const updateLabel = useCallback(
    async (code: string, displayLabel: string): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          await updateResignReason(code, displayLabel);
          await reload();
          ok = true;
          return true;
        },
        successMessage: "Reason updated.",
        errorMessage: "Could not update the reason.",
      });
      return ok;
    },
    [reload, toastActionWrapper]
  );

  const deactivate = useCallback(
    async (code: string) => {
      await toastActionWrapper({
        action: async () => {
          await deactivateResignReason(code);
          await reload();
          return true;
        },
        successMessage: "Reason deactivated.",
        errorMessage: "Could not deactivate the reason.",
      });
    },
    [reload, toastActionWrapper]
  );

  return { reasons, loading, error, reload, create, updateLabel, deactivate };
};

export default useResignReasons;
